import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { apiWordPattern } from '../utils/api';
import SeoContent from '../components/SeoContent';
import DefinableWord from '../components/DefinableWord';
import PageSEO from '../components/PageSEO';

export default function WordPatternFinder() {
  const [pattern, setPattern] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  function handleInput(val) {
    setPattern(val.replace(/[^a-zA-Z?_]/g, '').toUpperCase());
  }

  async function handleSolve(e) {
    e.preventDefault();
    if (!pattern.trim()) return;
    setLoading(true); setError(''); setResults(null);
    try {
      const { data } = await apiWordPattern(pattern.toLowerCase());
      setResults(data.results);
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally { setLoading(false); }
  }

  const EXAMPLES = [
    { label: 'C?T', desc: 'CAT, CUT, COT…' },
    { label: '?IGHT', desc: 'LIGHT, NIGHT, MIGHT…' },
    { label: 'TR??K', desc: 'TRACK, TRICK, TRUCK…' },
    { label: 'S???T', desc: 'SHIRT, SHORT, SPORT…' },
  ];

  return (
    <div className="game-page">
      <PageSEO page="word-pattern" />
      <div className="container" style={{maxWidth:700}}>
        <Link to="/" className="back-btn"><i className="bi bi-arrow-left"></i> All Games</Link>

        <div className="game-header">
          <span className="game-icon">🔎</span>
          <h1>Word Pattern Finder</h1>
          <p>Use <strong>?</strong> for unknown letters. Find every word matching your pattern!</p>
        </div>

        <div className="solver-card">
          <div className="d-flex flex-wrap gap-2 mb-3">
            {EXAMPLES.map(ex => (
              <button key={ex.label} type="button" className="btn btn-sm fw-bold"
                style={{borderRadius:50, background:'var(--border)', border:'none', fontFamily:'Nunito,sans-serif'}}
                onClick={() => setPattern(ex.label)}>
                {ex.label} <span className="text-muted fw-normal ms-1">({ex.desc})</span>
              </button>
            ))}
          </div>
          <form onSubmit={handleSolve}>
            <div className="mb-3">
              <label className="fw-bold mb-1">Pattern (<code>?</code> = any letter)</label>
              <input
                className="big-input"
                style={{letterSpacing:6, textTransform:'uppercase'}}
                value={pattern}
                onChange={e => handleInput(e.target.value)}
                placeholder="e.g.  ? I G H T"
                maxLength={20}
                autoFocus
              />
              <small className="text-muted">{pattern.length} letters · {pattern.split('').filter(c => c === '?').length} unknown</small>
            </div>
            <button type="submit" className="solve-btn" disabled={loading || !pattern}>
              {loading ? <><span className="spinner-border spinner-border-sm me-2"></span>Searching…</> : '🔎 Find Matches'}
            </button>
          </form>
        </div>

        {error && <div className="alert alert-danger alert-custom">{error}</div>}

        {results !== null && (
          <div className="solver-card">
            <p className="result-count">
              Found <strong>{results.length}</strong> match{results.length !== 1 ? 'es' : ''} for <strong>{pattern}</strong>
            </p>
            {results.length === 0 ? (
              <p className="text-muted">No matches found. Try a different pattern!</p>
            ) : (
              <div className="words-cloud">
                {results.map(r => {
                  const word = typeof r === 'string' ? r : r.word;
                  return (
                    <DefinableWord key={word} word={word} className="result-badge" style={{fontFamily:'monospace', letterSpacing:2, textDecoration:'none'}}>
                      {word.toUpperCase().split('').map((ch, i) => (
                        <span key={i} style={pattern[i] === '?' ? {color:'var(--primary)', fontWeight:900} : {}}>
                          {ch}
                        </span>
                      ))}
                    </DefinableWord>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <SeoContent game="word-pattern" />
      </div>
    </div>
  );
}
